import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckIcon } from 'lucide-react';

import { ApiError, api, type PluginDescriptor, type ScoringConfig } from '@/lib/api';
import { InfoHint } from '@/components/info-hint';
import { useT } from '@/lib/i18n';
import { notify } from '@/lib/notify';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

/**
 * The module create wizard (`plan-beta` §6).
 *
 * THE DISTINCTION THIS SCREEN EXISTS TO MAKE. A module binds a `modelType`, and
 * a model is a **plugin** — code. So "add a module" is two entirely different
 * requests wearing one phrase:
 *
 *   - a new module reusing a registered model → **data only**, and this is it;
 *   - a new *psychometric model* → **code**, a plugin file, deployed. `11 Flow
 *     1` specifies a package → sandbox → review → register pipeline and none of
 *     it is built; sandboxing untrusted scoring code is a security project, not
 *     a screen.
 *
 * The wizard is explicit about that rather than leaving an operator to discover
 * it — the model list below is exactly what is deployed, and if the model you
 * want is not in it, no form on this platform can add it.
 *
 * EVERYTHING COMES FROM THE REGISTRY. The models, their supported item types and
 * the starting config are all read from `GET /api/plugins`. A hardcoded list
 * here would be a fourth model that ships and cannot be selected — breaking the
 * "one plugin file, zero UI edits" property this architecture is sold on, in the
 * screen that exists to use it.
 *
 * A MODULE IS CREATED AS A DRAFT, always. It has no items yet, so publishing it
 * straight into `active` would put an empty bank in front of the next candidate
 * of every persona whose plan names it. Publishing is a separate, deliberate act
 * that reports the coverage it is publishing into.
 */

const MODULE_ID = /^[a-z][a-z0-9_]{2,63}$/;

interface Draft {
  moduleId: string;
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  estimatedMinutes: number;
  order: number;
}

const EMPTY: Draft = {
  moduleId: '',
  nameEn: '',
  nameAr: '',
  descriptionEn: '',
  descriptionAr: '',
  estimatedMinutes: 15,
  order: 0,
};

function Step({ n, title, hint }: { n: number; title: string; hint: string }) {
  return (
    <div>
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <span className="bg-muted flex size-5 items-center justify-center rounded-full text-xs">{n}</span>
        {title}
      </h3>
      <p className="text-muted-foreground mt-0.5 text-xs">{hint}</p>
    </div>
  );
}

export function CreateModuleWizard({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const t = useT();
  const plugins = useQuery({ queryKey: ['plugins'], queryFn: api.plugins });

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [modelType, setModelType] = useState<string | null>(null);
  const [types, setTypes] = useState<string[]>([]);
  const [configText, setConfigText] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const chosen = plugins.data?.items.find((p) => p.modelType === modelType) ?? null;

  /**
   * Choosing a model resets the two steps that depend on it.
   *
   * Carrying the previous model's item types forward would offer `likert` under
   * IRT_BARS — a selection the server rejects, presented as if it were valid.
   */
  function chooseModel(plugin: PluginDescriptor) {
    setModelType(plugin.modelType);
    setTypes([...plugin.supportedItemTypes]);
    setConfigText(plugin.configTemplate ? JSON.stringify(plugin.configTemplate, null, 2) : '');
    setErrors([]);
  }

  const create = useMutation({
    mutationFn: async () => {
      const scoringConfig = JSON.parse(configText) as ScoringConfig;
      return api.createModule({
        moduleId: draft.moduleId,
        name: { en: draft.nameEn, ar: draft.nameAr },
        description: { en: draft.descriptionEn, ar: draft.descriptionAr },
        modelType: chosen!.modelType,
        // Both from the registry, never typed: `pluginVersion` is stamped on
        // every result as `instrumentVersion`, and a value an operator could
        // mistype would misattribute which build produced a score.
        pluginId: chosen!.pluginId,
        pluginVersion: chosen!.version,
        estimatedMinutes: draft.estimatedMinutes,
        status: 'draft',
        order: draft.order,
        supportedItemTypes: types,
        scoringConfig,
      });
    },
    onSuccess: (module) => {
      notify.success(
        t('createModule.created', { module: module.moduleId }),
        t('createModule.created.detail'),
      );
      void queryClient.invalidateQueries({ queryKey: ['modules'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof SyntaxError) {
        setErrors([t('createModule.badJson', { error: err.message })]);
        return;
      }
      const details = err instanceof ApiError ? (err.payload.details as { errors?: string[] } | undefined) : undefined;
      setErrors(details?.errors ?? [err instanceof Error ? err.message : t('createModule.failed')]);
    },
  });

  const idValid = MODULE_ID.test(draft.moduleId);
  const ready =
    idValid
    && draft.nameEn.trim() !== ''
    && draft.nameAr.trim() !== ''
    && draft.descriptionEn.trim() !== ''
    && draft.descriptionAr.trim() !== ''
    && chosen !== null
    && types.length > 0
    && configText.trim() !== '';

  return (
    <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{t('createModule.title')}</DialogTitle>
        <DialogDescription>{t('createModule.description')}</DialogDescription>
      </DialogHeader>

      <div className="space-y-6">
        {/* ------------------------------------------------------- 1. identity */}
        <section className="space-y-3">
          <Step n={1} title={t('createModule.step1')} hint={t('createModule.step1.hint')} />

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="moduleId">
                {t('createModule.moduleId')}
                <InfoHint>{t('createModule.moduleId.detail')}</InfoHint>
              </Label>
              <Input
                id="moduleId"
                value={draft.moduleId}
                placeholder="workplace_values"
                onChange={(e) => set('moduleId', e.target.value)}
                className="font-mono"
                dir="ltr"
              />

              {draft.moduleId !== '' && !idValid && (
                <p className="text-destructive text-xs">{t('createModule.moduleId.invalid')}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="minutes">{t('createModule.minutes')}</Label>
              <Input
                id="minutes"
                type="number"
                min={1}
                value={draft.estimatedMinutes}
                onChange={(e) => set('estimatedMinutes', Number(e.target.value))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="nameEn">{t('createModule.name.en')}</Label>
              <Input
                id="nameEn" dir="ltr" value={draft.nameEn}
                onChange={(e) => set('nameEn', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="nameAr">{t('createModule.name.ar')}</Label>
              <Input
                id="nameAr"
                dir="rtl"
                value={draft.nameAr}
                onChange={(e) => set('nameAr', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="descEn">{t('createModule.description.en')}</Label>
              <Textarea
                id="descEn"
                dir="ltr"
                value={draft.descriptionEn}
                onChange={(e) => set('descriptionEn', e.target.value)}
                className="h-20"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="descAr">{t('createModule.description.ar')}</Label>
              <Textarea
                id="descAr"
                dir="rtl"
                value={draft.descriptionAr}
                onChange={(e) => set('descriptionAr', e.target.value)}
                className="h-20"
              />
            </div>
          </div>
        </section>

        {/* --------------------------------------------------------- 2. model */}
        <section className="space-y-3">
          <Step
            n={2}
            title={t('createModule.step2')}
            hint={t('createModule.step2.hint')}
          />

          {plugins.isLoading && <Skeleton className="h-24 w-full" />}

          {plugins.data && (
            <div className="grid gap-2 sm:grid-cols-2">
              {plugins.data.items.map((plugin) => (
                <button
                  key={plugin.modelType}
                  type="button"
                  onClick={() => chooseModel(plugin)}
                  className={`rounded-md border p-3 text-start text-sm transition ${
                    modelType === plugin.modelType ? 'border-primary bg-accent' : 'hover:bg-accent/50'
                  }`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="font-medium">{plugin.displayName}</span>
                    {modelType === plugin.modelType && <CheckIcon className="size-4 shrink-0" />}
                  </span>
                  <span className="text-muted-foreground mt-1 block font-mono text-xs" dir="ltr">
                    {plugin.modelType} · v{plugin.version}
                  </span>
                  <span className="text-muted-foreground mt-1 block text-xs">
                    {t('createModule.scores', { types: plugin.supportedItemTypes.join(', ') })}
                    {plugin.retainedVersions.length > 1
                      && ` · ${t('createModule.buildsRetained', { count: plugin.retainedVersions.length })}`}
                  </span>
                </button>
              ))}
            </div>
          )}

          {plugins.data?.items.length === 0 && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{t('createModule.noPlugins')}</AlertDescription>
            </Alert>
          )}
        </section>

        {/* ---------------------------------------------------- 3. item types */}
        {chosen && (
          <section className="space-y-3">
            <Step
              n={3}
              title={t('createModule.step3')}
              hint={t('createModule.step3.hint')}
            />
            <div className="flex flex-wrap gap-3">
              {chosen.supportedItemTypes.map((type) => (
                <label key={type} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={types.includes(type)}
                    onCheckedChange={(on) =>
                      setTypes((current) =>
                        on ? [...current, type] : current.filter((t) => t !== type),
                      )
                    }
                  />
                  <span className="font-mono" dir="ltr">{type}</span>
                </label>
              ))}
            </div>
            {types.length === 0 && (
              <p className="text-destructive text-xs">{t('createModule.needAType')}</p>
            )}
          </section>
        )}

        {/* -------------------------------------------------- 4. scoring config */}
        {chosen && (
          <section className="space-y-3">
            <Step
              n={4}
              title={t('createModule.step4')}
              hint={t('createModule.step4.hint')}
            />

            {chosen.configTemplate === null && (
              <Alert>
                <AlertDescription>
                  {t('createModule.noTemplate', { model: chosen.displayName })}
                </AlertDescription>
              </Alert>
            )}

            <Textarea
              value={configText}
              onChange={(e) => setConfigText(e.target.value)}
              spellCheck={false}
              className="h-64 font-mono text-xs"
              aria-label={t('config.jsonLabel')}
            />
            <p className="text-muted-foreground flex items-center gap-1 text-xs">
              <Badge variant="outline" className="me-1">
                {t('createModule.version1')}
              </Badge>
              {t('createModule.versionShort')}
              <InfoHint>{t('createModule.versionNote')}</InfoHint>
            </p>
          </section>
        )}

        {errors.length > 0 && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              <p className="mb-1 font-medium">{t('createModule.nothingCreated')}</p>
              <ul className="list-inside list-disc">
                {errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button onClick={() => create.mutate()} disabled={!ready || create.isPending}>
          {create.isPending ? t('users.creating') : t('createModule.createDraft')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
